/**
 * Automation Page Component
 * Allows admin users to create and manage automated report generation
 */

import React, { useState, useEffect } from 'react';
import { canEditClaims } from '../../utils/roles';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Box,
  Switch,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MDBox } from '../../components/MDBox';
import { MDTypography } from '../../components/MDTypography';
import { MDButton } from '../../components/MDButton';
import { ContextPanel } from '../../components/ContextPanel';
import { useAuthContext } from '../../contexts/authContext';
import { logger } from '../../services/logger';
import {
  automationService,
  ReportAutomation,
} from '../../services/automationService';
import { ReportAutomationForm } from './ReportAutomationForm';
import AutomationIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import CloudIcon from '@mui/icons-material/Cloud';
import RefreshIcon from '@mui/icons-material/Refresh';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  actionCell: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[700],
  },
  formatChip: {
    marginRight: theme.spacing(0.5),
  },
  deliveryIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

export function AutomationPage() {
  const classes = useStyles();
  const { user } = useAuthContext();
  const [reports, setReports] = useState<ReportAutomation[]>([]);
  const [loading, setLoading] = useState(true);
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportAutomation | null>(
    null
  );
  const [refreshing, setRefreshing] = useState(false);

  // Check if user has admin role
  const isAdmin = canEditClaims(user?.role);
  console.log('User is admin:', isAdmin);
  useEffect(() => {
    if (isAdmin) {
      loadReports();
    }
  }, [isAdmin]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await automationService.getReportAutomations();
      setReports(data);
    } catch (error) {
      logger.error('Failed to load report automations', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadReports();
    setRefreshing(false);
  };

  const handleCreateNew = () => {
    setSelectedReport(null);
    setContextPanelOpen(true);
  };

  const handleEdit = (report: ReportAutomation) => {
    setSelectedReport(report);
    setContextPanelOpen(true);
  };

  const handleDelete = async (reportId: number) => {
    if (
      window.confirm('Are you sure you want to delete this report automation?')
    ) {
      try {
        await automationService.deleteReportAutomation(reportId);
        await loadReports();
      } catch (error) {
        logger.error('Failed to delete report automation', {
          error: error instanceof Error ? error.message : String(error),
          reportId: String(reportId),
        });
      }
    }
  };

  const handleToggleActive = async (reportId: number) => {
    try {
      await automationService.toggleReportAutomation(reportId);
      await loadReports();
    } catch (error) {
      logger.error('Failed to toggle report automation', {
        error: error instanceof Error ? error.message : String(error),
        reportId: String(reportId),
      });
    }
  };

  const handleFormSubmit = async () => {
    setContextPanelOpen(false);
    await loadReports();
  };

  const formatFrequency = (
    frequency: string,
    hour?: number,
    dayOfMonth?: number,
    dayOfWeek?: number
  ) => {
    let result = frequency.charAt(0).toUpperCase() + frequency.slice(1);

    if (frequency === 'hourly') {
      return result;
    }

    if (hour !== undefined) {
      const hourStr = hour.toString().padStart(2, '0');
      result += ` at ${hourStr}:00 UTC`;
    }

    if (frequency === 'weekly' && dayOfWeek !== undefined) {
      const days = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      result += ` on ${days[dayOfWeek]}`;
    }

    if (frequency === 'monthly' && dayOfMonth !== undefined) {
      result += ` on day ${dayOfMonth}`;
    }

    return result;
  };

  const formatNextRun = (nextRunAt?: string) => {
    if (!nextRunAt) return 'Not scheduled';

    const date = new Date(nextRunAt);
    return date.toLocaleString();
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg">
        <MDBox py={3}>
          <MDTypography variant="h5" color="error">
            Access Denied
          </MDTypography>
          <MDTypography variant="body1" color="text" mt={2}>
            You need admin privileges to access this page.
          </MDTypography>
        </MDBox>
      </Container>
    );
  }

  return (
    <MDBox className={classes.root}>
      {reports.length !== 0 && (
        <Box className={classes.header}>
          <MDBox display="flex" alignItems="center">
            <MDTypography variant="h3" fontWeight="medium">
              Report Automation
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="outlined"
              color="info"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              New Report
            </MDButton>
          </MDBox>
        </Box>
      )}

      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Paper elevation={2}>
          <MDBox className={classes.emptyState}>
            <MDTypography variant="h6" color="text" mb={2}>
              No automated reports configured
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={3}>
              Get started by creating your first automated report.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Create First Report
            </MDButton>
          </MDBox>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      width={40}
                    >
                      {report.name}
                    </MDTypography>
                    {report.description && (
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        width={40}
                      >
                        {report.description}
                      </MDTypography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={report.sourceTable} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={report.format}
                      size="small"
                      className={classes.formatChip}
                      color={report.format === 'CSV' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <MDBox display="flex" alignItems="center">
                      {report.deliveryChannel === 'email' ? (
                        <>
                          <EmailIcon className={classes.deliveryIcon} />
                          <MDTypography variant="caption">
                            Email ({report.emailRecipients.length})
                          </MDTypography>
                        </>
                      ) : (
                        <>
                          <CloudIcon className={classes.deliveryIcon} />
                          <MDTypography variant="caption">SFTP</MDTypography>
                        </>
                      )}
                    </MDBox>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant="caption">
                      {formatFrequency(
                        report.frequency,
                        report.scheduleHour,
                        report.scheduleDayOfMonth,
                        report.scheduleDayOfWeek
                      )}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant="caption">
                      {formatNextRun(report.nextRunAt)}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={report.isActive}
                      onChange={() => handleToggleActive(report.id)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actionCell}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(report)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(report.id)}
                          color="default"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ContextPanel
        open={contextPanelOpen}
        onClose={() => setContextPanelOpen(false)}
        side="right"
        size="medium"
        title={
          selectedReport ? 'Edit Report Automation' : 'New Report Automation'
        }
      >
        <ReportAutomationForm
          report={selectedReport}
          onSubmit={handleFormSubmit}
          onCancel={() => setContextPanelOpen(false)}
        />
      </ContextPanel>
    </MDBox>
  );
}
